// /admin/integrations — what this platform is connected to, and what is allowed to
// connect to it.
//
// THE REASON THIS PAGE EXISTS, in the user's own words: "please create an integrations/api
// button to the admin panel where integrations can be configured like this so that if we
// want to remove this from inside the platform or add a new one from inside the platform
// we can." Everything below is drawn from `kinds` — the registry's own description of
// itself (src/integrations/registry.js) — so a kind added there gets a card, a form and an
// Add entry here with no change to this file. That is the requirement, not a nicety.
//
// NOT A DESCRIPTOR, and never listed by GET /api/admin, for the same shape of reason
// Settings is not: the generic framework's six routes are keyed on a row id and write one
// table with a fixed column set, and this screen writes a sealed credential blob, reads a
// registry the server holds, and calls two endpoints (`/test`, `/api-clients`) the
// framework has no vocabulary for.
//
// PRESENTATION, NEVER AUTHORIZATION. `assertRank(req, { requires: 'admin' })` on
// src/routes/api/admin/index.js is what refuses; this component mounts happily for a lead
// who types the URL and reports what the API said.
//
// No `mx-auto max-w-…` wrapper: AppLayout's <main> already is that container.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { apiFetch, ApiError } from '../../api/client.js';
import { useAtLeastRole } from '../../session/SessionProvider.jsx';
import { formatDateTime, formatRelative } from '../../components/tickets/format.js';
import { consoleErrorMessage, mapFieldErrors, refusalBanner } from './descriptor-form.js';
import { healthLine, secretsKeyBanner } from './integrations-form.js';

/** The banner's two sentences. One component, because both faults print the same way. */
function KeyBanner({ banner }) {
  if (!banner) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950">
      <p className="text-sm font-medium text-amber-900 dark:text-amber-200">{banner.headline}</p>
      <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">{banner.detail}</p>
    </div>
  );
}

/**
 * One configured integration.
 *
 * The whole row is a link, as the console index's cards are — an admin who wants to know
 * anything more than "is it on" is going to the edit view regardless.
 */
function IntegrationCard({ row, kind }) {
  const health = healthLine(row.status, formatDateTime);

  return (
    <li>
      <Link
        to={`/admin/integrations/${encodeURIComponent(row.id)}`}
        className="block rounded-lg border border-line bg-surface p-4 hover:bg-sunken
                   focus-visible:outline-2 focus-visible:outline-offset-2
                   focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-ink">{row.name}</span>
          <span className="flex items-center gap-2">
            {/* The kind is what the row IS; the label is only how the registry spells
                it, so a row whose kind was removed still names its own kind. */}
            <span className="badge badge-new">{kind?.label ?? row.kind}</span>
            <span className={row.is_enabled ? 'badge badge-solved' : 'badge badge-closed'}>
              {row.is_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </span>
        </div>

        <p className="mt-1 text-xs text-ink-secondary">
          {/* "Nothing yet" is a real answer and a common one — a row that has been
              created but never enabled has written no status at all. */}
          {health.text ?? 'No activity reported yet.'}
        </p>
        {health.error && (
          <p className="mt-1 text-xs text-red-700 dark:text-red-300">
            Last error: {health.error}
          </p>
        )}
        {!kind && (
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
            This build no longer knows the kind "{row.kind}". It can be renamed, disabled
            and deleted here, but not configured.
          </p>
        )}
      </Link>
    </li>
  );
}

/**
 * Partner API keys — the `api_clients` table, which has existed since migration 001 and
 * has never had a screen.
 *
 * ON THIS PAGE rather than one of its own because it is the same question the rest of the
 * page asks, from the other side: what is this platform allowed to talk to, and what is
 * allowed to talk to this platform. Rendered from the registry's `api-keys` entry
 * (`manages_rows: false`), so it appears because the registry declares it and not because
 * this component hardcodes a section.
 *
 * THERE IS DELIBERATELY NO RENAME. `api_clients.name` is the partner's TICKET ID
 * NAMESPACE, not a label — `partnerSourceForClient` derives `partner_tickets.source` from
 * it — so renaming a client re-namespaces it and the partner's next delivery of an
 * already-shared ticket becomes a duplicate. The server refuses to offer one; neither
 * does this.
 */
function ApiClients({ kind, mayWrite }) {
  const [clients, setClients] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  // The plaintext key, for as long as this screen stays open. It is not in state that
  // survives a reload because it does not exist anywhere that could reload it.
  const [issued, setIssued] = useState(null);

  const load = useCallback((signal) => apiFetch('/admin/api-clients', { signal })
    .then((body) => setClients(body.data)), []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal).catch((err) => {
      if (err?.name !== 'AbortError') setError(consoleErrorMessage(err));
    });
    return () => controller.abort();
  }, [load]);

  async function issue() {
    setBusy(true);
    setError(null);
    try {
      const body = await apiFetch('/admin/api-clients', { method: 'POST', body: { name: name.trim() } });
      // Shown ONCE. `POST /api/admin/api-clients` is the only response that ever carries
      // the plaintext and there is no endpoint that returns it again — only the SHA-256
      // digest is stored, so a lost key is re-issued rather than looked up.
      setIssued({ name: body.data.name, key: body.data.key });
      setName('');
      setAdding(false);
      await load();
    } catch (err) {
      if (err instanceof ApiError && Array.isArray(err.details?.fields)) {
        const { formErrors } = mapFieldErrors({ fields: {} }, err.details.fields);
        setError(refusalBanner({}, formErrors).headline ?? consoleErrorMessage(err));
      } else {
        setError(consoleErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function revoke(client) {
    if (!window.confirm(
      `Revoke "${client.name}"? Anything still using this key stops being able to open `
      + 'tickets immediately, and the key cannot be un-revoked.',
    )) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/admin/api-clients/${encodeURIComponent(client.id)}/revoke`, { method: 'POST' });
      await load();
    } catch (err) {
      setError(consoleErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-ink">{kind.label}</h2>
        {mayWrite && !adding && (
          <button type="button" className="btn-ghost ring-1 ring-line-strong" onClick={() => setAdding(true)}>
            Issue key
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-ink-secondary">{kind.description}</p>

      {error && (
        <p className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700
                      dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {error}
        </p>
      )}

      {issued && (
        <div className="mt-3 rounded-md border border-green-300 bg-green-50 p-3
                        dark:border-green-900 dark:bg-green-950">
          <p className="text-xs font-medium text-green-900 dark:text-green-200">
            Key for "{issued.name}". THIS IS THE ONLY TIME IT IS SHOWN — only its hash is
            stored, so a key that is lost is re-issued, never looked up.
          </p>
          {/* A readonly input rather than a <code> block: it can be selected with one
              click and copied with the keyboard, on every browser, with no clipboard
              permission in play. */}
          <input
            readOnly
            className="field-input mt-2 font-mono text-xs"
            value={issued.key}
            onFocus={(event) => event.target.select()}
            onClick={(event) => event.target.select()}
          />
          <button
            type="button"
            className="btn-ghost mt-2 px-2 py-0.5 text-xs ring-1 ring-line-strong"
            onClick={() => setIssued(null)}
          >
            I have saved it
          </button>
        </div>
      )}

      {adding && (
        <div className="mt-3 rounded-md border border-line bg-sunken p-3">
          <label className="field-label" htmlFor="api-client-name">Partner name</label>
          <input
            id="api-client-name"
            className="field-input"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <p className="mt-1 text-xs text-ink-muted">
            This is the partner's ticket-id namespace, not a label — it is what
            `partner_tickets.source` is derived from — so it cannot be changed afterwards.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              className="btn-primary"
              disabled={busy || name.trim() === ''}
              onClick={() => { void issue(); }}
            >
              {busy ? 'Issuing…' : 'Issue key'}
            </button>
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              onClick={() => { setAdding(false); setName(''); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {clients === null && !error && <p className="mt-3 text-sm text-ink-muted">Loading…</p>}
      {clients?.length === 0 && (
        <p className="mt-3 text-sm text-ink-muted">No partner keys have been issued.</p>
      )}

      {clients?.length > 0 && (
        <ul className="mt-3 divide-y divide-line border-t border-line">
          {clients.map((client) => (
            <li key={client.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm text-ink">{client.name}</p>
                <p className="text-xs text-ink-muted">
                  {client.scopes.join(', ') || 'no scopes'}
                  {' · '}
                  {/* The question that makes rotation less frightening: is anything
                      still using this key? */}
                  {client.last_used_at
                    ? `last used ${formatRelative(client.last_used_at)}`
                    : 'never used'}
                </p>
              </div>
              <span className="flex items-center gap-2">
                <span className={client.is_active ? 'badge badge-solved' : 'badge badge-closed'}>
                  {client.is_active ? 'Active' : 'Revoked'}
                </span>
                {mayWrite && client.is_active && (
                  <button
                    type="button"
                    className="btn-ghost px-2 py-0.5 text-xs ring-1 ring-line-strong"
                    disabled={busy}
                    onClick={() => { void revoke(client); }}
                  >
                    Revoke
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function AdminIntegrations() {
  const navigate = useNavigate();
  // PRESENTATION, NEVER AUTHORIZATION (SPEC.md §7.3). The routes are admin-only, so in
  // practice anyone who sees this page holds it; this only decides whether a control
  // whose single possible outcome is a 403 is drawn at all — which reads as a broken app
  // rather than as a boundary.
  const mayWrite = useAtLeastRole('support', 'admin');
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    apiFetch('/admin/integrations', { signal: controller.signal })
      .then((body) => setState(body))
      .catch((err) => { if (err?.name !== 'AbortError') setError(consoleErrorMessage(err)); });
    return () => controller.abort();
  }, []);

  if (error) {
    return (
      <div className="max-w-3xl">
        <Link to="/admin" className="text-sm text-ink-secondary hover:underline">&larr; Admin</Link>
        <div className="mt-4 rounded-lg border border-line-strong bg-sunken p-4 text-sm text-ink">
          {error}
        </div>
      </div>
    );
  }
  if (!state) return <p className="text-sm text-ink-muted">Loading…</p>;

  const kinds = state.kinds ?? [];
  const byKind = new Map(kinds.map((entry) => [entry.kind, entry]));
  // `manages_rows: false` is the registry saying "my records are not `integrations` rows"
  // — `api-keys` owns `api_clients`. Such a kind has no Add entry and no edit form; it
  // renders its own section below.
  const addable = kinds.filter((entry) => entry.manages_rows !== false);
  const sections = kinds.filter((entry) => entry.manages_rows === false);

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <Link to="/admin" className="text-sm text-ink-secondary hover:underline">&larr; Admin</Link>
        <h1 className="mt-1 text-xl font-semibold tracking-tight text-ink">Integrations &amp; API</h1>
        <p className="text-sm text-ink-secondary">
          What this platform is connected to, and what is allowed to connect to it. An
          integration can be added, tested, turned off and removed from here — nothing on
          this page needs a deploy.
        </p>
      </div>

      <KeyBanner banner={secretsKeyBanner(state)} />

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-ink">Configured integrations</h2>
          {mayWrite && addable.length > 0 && (
            <span className="flex items-center gap-2">
              {/* The Add control IS the registry's list. A kind added to
                  src/integrations/registry.js appears here with no change to this file,
                  which is the property the whole feature was asked for. */}
              <label className="sr-only" htmlFor="add-integration-kind">Kind</label>
              <select
                id="add-integration-kind"
                className="field-input w-auto"
                value={adding}
                onChange={(event) => setAdding(event.target.value)}
              >
                <option value="">Add integration…</option>
                {addable.map((entry) => (
                  <option key={entry.kind} value={entry.kind}>{entry.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="btn-primary"
                disabled={adding === ''}
                onClick={() => navigate(`/admin/integrations/new?kind=${encodeURIComponent(adding)}`)}
              >
                Add
              </button>
            </span>
          )}
        </div>

        {state.data.length === 0 ? (
          <p className="rounded-lg border border-dashed border-line-strong p-4 text-sm text-ink-muted">
            Nothing is connected yet. Add one above.
          </p>
        ) : (
          <ul className="grid gap-3">
            {state.data.map((row) => (
              <IntegrationCard key={row.id} row={row} kind={byKind.get(row.kind)} />
            ))}
          </ul>
        )}
      </section>

      {/* Rendered from the registry, not hardcoded: a `manages_rows: false` kind owns a
          table of its own and gets a section rather than an Add entry. */}
      {sections.map((entry) => (
        <ApiClients key={entry.kind} kind={entry} mayWrite={mayWrite} />
      ))}
    </div>
  );
}
